import AttachmentIcon from "@mui/icons-material/Attachment";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { ReactComponent as Magnifier } from "../../assets/magnifier.svg";
import { FaFileVideo } from "react-icons/fa6";
import {
  BsFileEarmark,
  BsFiletypeDoc,
  BsFiletypeDocx,
  BsFiletypePdf,
  BsFiletypePpt,
  BsFiletypePptx,
  BsFiletypeTxt,
  BsFiletypeXls,
  BsFiletypeXlsx
} from "react-icons/bs";
import {
  Box,
  Button,
  Collapse,
  FormHelperText,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Lightbox } from "react-modal-image";
import ReactPlayer from "react-player";
import { useStore } from "react-redux";
import { useLocation } from "react-router-dom";
import { TransitionGroup } from "react-transition-group";
import { makeStyles } from "tss-react/mui";
import { v4 as uuidv4 } from "uuid";
import photoPlaceholder from "../../assets/photo-placeholder.svg";
import { config_UPTIME } from "../../Constants";
import DeletePopupByNodeId from "../../components/DeletePopupByNodeId";
import {
  allow_docs_mimetypes,
  allow_images_mimetypes,
  allow_videos_mimetypes,
  getFileExtention,
  truncateWithEllipses
} from "../../utils/helpers";
import { getItem } from "../../utils/DataProtection";

const useStyles = makeStyles()(theme => ({
  root: {
    width: "100%"
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.6,
    letterSpacing: "0.25px",
    color: "rgba(0, 0, 0, 0.87)",
    marginBottom: 12
  },
  dropZone: {
    border: "1px dashed rgba(0, 0, 0, 0.12)",
    borderRadius: 4,
    padding: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
    flexWrap: "wrap",
    transition: "border-color 0.2s ease, background-color 0.2s ease"
  },
  dropZoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(56, 116, 141, 0.04)"
  },
  dropZoneError: {
    borderColor: "#FF3D00",
    backgroundColor: "rgba(255, 61, 0, 0.02)"
  },
  dropZoneLeft: {
    display: "flex",
    alignItems: "center",
    gap: 24,
    minWidth: 0
  },
  dropZoneTextBlock: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0
  },
  dropZoneIcon: {
    color: "rgba(0, 0, 0, 0.5)"
  },
  dropZoneText: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: "0.25px",
    color: "rgba(0, 0, 0, 0.87)",
    marginBottom: "0px !important"
  },
  dropZoneSupportingText: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: "0.4px",
    color: "rgba(0, 0, 0, 0.5)"
  },
  actionButton: {
    minHeight: 36,
    whiteSpace: "nowrap"
  },
  hiddenInput: {
    display: "none"
  },
  list: {
    marginTop: 12,
    width: "100%"
  },
  transitionItem: {
    width: "100%"
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "12px 0",
    width: "100%"
  },
  rowErrorState: {
    backgroundColor: "#ECEFF1",
    borderRadius: 4,
    padding: 12
  },
  thumbnail: {
    width: 56,
    height: 56,
    borderRadius: 4,
    overflow: "hidden",
    position: "relative",
    backgroundColor: "#F5F5F5",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "1px solid rgba(0,0,0,0.08)",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block"
    }
  },
  thumbnailIcon: {
    color: "rgba(0, 0, 0, 0.45)",
    fontSize: 30
  },
  thumbnailIconUploading: {
    opacity: 0.22
  },
  uploadOverlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#38748D",
    opacity: 0.82,
    transition: "height 0.5s ease",
    pointerEvents: "none"
  },
  uploadOverlayLabel: {
    position: "absolute",
    left: "50%",
    bottom: 6,
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: "0.4px",
    color: "#FFFFFF",
    textShadow: "0 1px 2px rgba(0,0,0,0.25)",
    pointerEvents: "none"
  },
  rowContent: {
    flex: "1 1 auto",
    minWidth: 0
  },
  fileName: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: "0.4px",
    color: "#38748D",
    marginBottom: 5,
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    margin: "0px !important"
  },
  descriptionField: {
    width: "100%",
    "& .MuiInputBase-input": {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: "0.5px",
      color: "rgba(0, 0, 0, 0.87)",
      padding: "0 0 2px 0"
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(0, 0, 0, 0.5)",
      opacity: 1
    }
  },
  rowActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexShrink: 0
  },
  deleteButton: {
    color: "#FF3D00",
    padding: 4
  },
  dragHandle: {
    color: "rgba(0,0,0,0.32)"
  },
  readonlySection: {
    marginTop: 32
  },
  readonlyGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    marginTop: 12
  },
  readonlyPhotoCard: {
    width: 260
  },
  readonlyPhotoThumb: {
    width: "100%",
    height: 170,
    borderRadius: 4,
    overflow: "hidden",
    cursor: "pointer",
    position: "relative",
    border: "1px solid rgba(0,0,0,0.08)",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block"
    },
    "&:hover .attachment-zoom-fab": {
      opacity: 1
    }
  },
  readonlyVideoCard: {
    width: 260
  },
  readonlyVideoFrame: {
    position: "relative",
    width: 260,
    height: 170,
    borderRadius: 4,
    overflow: "hidden"
  },
  readonlyDescription: {
    marginTop: 10,
    fontSize: 14,
    lineHeight: 1.4,
    letterSpacing: "0.25px",
    color: "rgba(0,0,0,0.6)"
  },
  zoomFab: {
    position: "absolute",
    right: 8,
    bottom: 8,
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    boxShadow:
      "0px 2px 2px rgba(0,0,0,0.14), 0px 4px 2.5px rgba(0,0,0,0.12), 0px 1px 5px rgba(0,0,0,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "opacity 0.2s ease",
    pointerEvents: "none",
    color: "rgba(0,0,0,0.54)"
  },
  showMore: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: "#38748D",
    marginTop: 12,
    cursor: "pointer",
    fontSize: 14,
    lineHeight: 1.6,
    letterSpacing: "0.25px"
  },
  readonlyDocRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 0",
    cursor: "pointer"
  },
  readonlyDocThumb: {
    width: 56,
    height: 56,
    borderRadius: 4,
    border: "1px solid rgba(0,0,0,0.08)",
    backgroundColor: "#F7F9FC",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden"
  },
  readonlyDocIcon: {
    color: "#38748D",
    fontSize: 30,
    marginTop: 2
  },
  snackbarPaper: {
    width: 345,
    backgroundColor: "#FFFFFF",
    border: "2px solid #6FA03B",
    borderRadius: 4,
    boxShadow:
      "0px 6px 4px rgba(0,0,0,0.08), 0px 1px 9px rgba(0,0,0,0.12), 0px 3px 2.5px rgba(0,0,0,0.12)",
    padding: 14
  },
  snackbarTitleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    alignContent: "center",
    alignItems:"center",

  "& p":{
    marginBottom: "0px !important"
  }
  },
  snackbarTitle: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: "0.15px",
    fontFamily: "'Bai Jamjuree', sans-serif",
    color: "#6FA03B"
  },
  warningSnackbarTitle: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: "0.15px",
    fontFamily: "'Bai Jamjuree', sans-serif",
    color: "#ED6C02"
  },
  snackbarActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 12
  },
  snackbarUndoButton: {
    minHeight: 36,
    padding: "0 8px",
    color: "#38748D",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.2,
    letterSpacing: "1.25px"
  }
}));

const allAllowedMimeTypes = [
  ...allow_docs_mimetypes,
  ...allow_images_mimetypes,
  ...allow_videos_mimetypes
];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const acceptValue = Array.from(new Set(allAllowedMimeTypes)).join(",");

const isVideoMimeType = (mimeType = "") => mimeType.startsWith("video/");
const isImageMimeType = (mimeType = "") =>
  allow_images_mimetypes.includes(mimeType);
const isDocMimeType = (mimeType = "") =>
  allow_docs_mimetypes.includes(mimeType);

const getAttachmentKind = (mimeType = "") => {
  if (isImageMimeType(mimeType)) return "image";
  if (isVideoMimeType(mimeType)) return "video";
  return "doc";
};

const isAllowedMimeType = (mimeType = "") =>
  isImageMimeType(mimeType) ||
  isVideoMimeType(mimeType) ||
  isDocMimeType(mimeType);

const getLocalizedValue = (value, language) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value?.[language] || value?.de || Object.values(value)[0] || "";
  }
  return "";
};

const getAttachmentFileByLanguage = (file, language) => {
  if (!file) return null;

  if (file.url) {
    return file;
  }

  if (typeof file !== "object") {
    return null;
  }

  return (
    file?.[language] ||
    file?.de ||
    Object.values(file).find(entry => entry?.url) ||
    null
  );
};

// Extracts the bucket id (first path segment after the host) from a signed file url.
// e.g. https://minio.../clientabcgmbh/247uptime.io/... -> "clientabcgmbh"
const extractBucketIdFromUrl = (url = "") => {
  if (!url) return "";
  try {
    const { pathname } = new URL(url);
    return pathname.split("/").filter(Boolean)[0] || "";
  } catch (error) {
    // Fallback for non-standard urls: strip protocol then take first segment after host
    const withoutProtocol = url.replace(/^[a-zA-Z]+:\/\//, "");
    const segments = withoutProtocol.split("/").filter(Boolean);
    return segments[1] || "";
  }
};

// Builds the payload entry for a deleted persisted attachment.
// Adds a `bucketid` (derived from the file url) to `file` and removes the `url`.
// When `fileOverride` is provided (from re-uploading the file to a fresh minio
// slot) its fileid/fileversion/bucketid replace the original values.
const buildDeletedAttachmentPayload = (
  attachmentWrapper,
  language,
  fileOverride
) => {
  const attachment = attachmentWrapper?.attachment;
  if (!attachment) return null;

  const resolvedFile =
    getAttachmentFileByLanguage(attachment.file, language) ||
    attachment.file ||
    {};
  const file = { ...resolvedFile };

  if (file.url) {
    file.bucketid = extractBucketIdFromUrl(file.url);
    delete file.url;
  }

  if (fileOverride) {
    if (fileOverride.fileid !== undefined) file.fileid = fileOverride.fileid;
    if (fileOverride.fileversion !== undefined)
      file.fileversion = fileOverride.fileversion;
    if (fileOverride.bucketid !== undefined)
      file.bucketid = fileOverride.bucketid;
  }

  return {
    title: attachment.title ?? null,
    description: attachment.description ?? null,
    file,
    nodeid: fileOverride?.nodeid || attachment.nodeid
  };
};

const normalizePersistedAttachment = (attachmentWrapper, language) => {
  const attachment = attachmentWrapper?.attachment;
  const file = getAttachmentFileByLanguage(attachment?.file, language);
  const mimeType = file?.mimetype || "";

  if (!attachment?.nodeid || !file?.url || !isAllowedMimeType(mimeType)) {
    return null;
  }

  return {
    id: attachment.nodeid,
    nodeid: attachment.nodeid,
    sourceType: "persisted",
    kind: getAttachmentKind(mimeType),
    previewUrl: file.url,
    fileName:
      getLocalizedValue(attachment?.title, language) ||
      file?.fileid?.split("/").pop() ||
      "attachment",
    title: getLocalizedValue(attachment?.title, language),
    description: getLocalizedValue(attachment?.description, language),
    mime: mimeType,
    raw: attachmentWrapper
  };
};

const buildPersistedAttachments = (props, location, language) => {
  if (
    props.notification &&
    props.attachmentsOf === "notification" &&
    props.notification?.notification?.hasattachment?.length > 0
  ) {
    return props.notification.notification.hasattachment
      .map(item => normalizePersistedAttachment(item, language))
      .filter(Boolean);
  }

  if (
    props.workflowAttachments &&
    props.workflowAttachments.length > 0 &&
    (props.attachmentsOf === "solution" ||
      props.attachmentsOf === "faq" ||
      props.attachmentsOf === "report" ||
      props.attachmentsOf === "maintenanceTask")
  ) {
    return props.workflowAttachments
      .map(item => normalizePersistedAttachment(item, language))
      .filter(Boolean);
  }

  if (
    location?.state?.workflowAttachments?.length > 0 &&
    props.from !== "maintenance_task_template"
  ) {
    return location.state.workflowAttachments
      .map(item => normalizePersistedAttachment(item, language))
      .filter(Boolean);
  }

  if (props.questionanswerAttachments?.length > 0) {
    return props.questionanswerAttachments
      .map(item => normalizePersistedAttachment(item, language))
      .filter(Boolean);
  }

  if (location?.state?.questionanswerAttachments?.length > 0) {
    return location.state.questionanswerAttachments
      .map(item => normalizePersistedAttachment(item, language))
      .filter(Boolean);
  }

  return [];
};

const normalizeDraftAttachmentItem = (item = {}, fallbackKind, index = 0) => {
  const mime = item.mime || "";
  const derivedKind = fallbackKind || getAttachmentKind(mime);
  const previewUrl =
    derivedKind === "image" ? item.url || item.previewUrl || "" : "";

  return {
    id:
      item.id ||
      item.nodeid ||
      `${derivedKind}-draft-${index}-${Math.random().toString(36).slice(2, 8)}`,
    nodeid: item.nodeid || "",
    index:
      item.index !== undefined ? item.index : Date.now() + index,
    sourceType: "local",
    kind: derivedKind,
    previewUrl,
    fileName:
      item.fileName ||
      item.title ||
      item.fileid?.split("/").pop() ||
      "attachment",
    title: item.title || "",
    description: item.description || "",
    mime,
    file: null,
    url: item.url || "",
    fileid: item.fileid || "",
    bucketid: item.bucketid || "",
    fileversion: item.fileversion || "",
    uploading: Boolean(item.uploading),
    uploaded:
      item.uploaded !== undefined ? Boolean(item.uploaded) : Boolean(item.url),
    uploadState:
      item.uploadState ||
      (item.uploadError
        ? "error"
        : item.uploaded || item.url
        ? "uploaded"
        : "preparing"),
    uploadProgress:
      item.uploadProgress !== undefined
        ? Number(item.uploadProgress) || 0
        : item.url
        ? 100
        : 0,
    uploadError: item.uploadError || ""
  };
};

const buildLocalDraftItems = draftAttachments => {
  if (!draftAttachments || typeof draftAttachments !== "object") return [];

  return [
    ...(draftAttachments.images || []).map((item, index) =>
      normalizeDraftAttachmentItem(item, "image", index)
    ),
    ...(draftAttachments.videos || []).map((item, index) =>
      normalizeDraftAttachmentItem(item, "video", index)
    ),
    ...(draftAttachments.docs || []).map((item, index) =>
      normalizeDraftAttachmentItem(item, "doc", index)
    )
  ];
};

const mapPendingToLegacyShape = items =>
  items.map(item => ({
    nodeid: item.nodeid,
    index: item.index,
    fileid: item.fileid,
    url: item.url,
    image: item.file,
    mime: item.mime,
    bucketid: item.bucketid,
    fileversion: item.fileversion,
    title: item.title,
    description: item.description,
    uploaded: item.uploaded,
    uploadProgress: item.uploadProgress,
    uploadError: item.uploadError
  }));

const buildChangedPersistedAttachments = (
  items,
  overrides,
  language
) =>
  items.flatMap(item => {
    const override = overrides[item.id];

    if (!override) return [];

    const currentTitle = item.title || "";
    const currentDescription = item.description || "";
    const nextTitle =
      override.title !== undefined ? override.title : currentTitle;
    const nextDescription =
      override.description !== undefined
        ? override.description
        : currentDescription;

    if (
      nextTitle === currentTitle &&
      nextDescription === currentDescription
    ) {
      return [];
    }

    return [
      {
        nodeid: item.nodeid,
        attachmentnodeid: item.nodeid,
        title: nextTitle,
        description: nextDescription,
        specifier: item.raw?.attachment?.specifier,
        lang: language
      }
    ];
  });

export const persistAttachmentMetadataUpdates = async (
  changes = [],
  language = "de"
) => {
  const updates = (changes || []).filter(change => change?.nodeid);

  if (!updates.length) return;

  const userToken = getItem("user");

  await Promise.all(
    updates.map(change =>
      axios.put(
        `${config_UPTIME.url.API_URL}/attachments/${encodeURIComponent(
          change.nodeid
        )}`,
        {
          lang: change.lang || language,
          title: change.title || "",
          description: change.description || "",
          specifier: change.specifier,
          attachmentnodeid: change.attachmentnodeid || change.nodeid
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + JSON.parse(userToken)
          }
        }
      )
    )
  );
};

export default function NotificationAttachments(props) {
  const { classes, cx } = useStyles();
  const routerLocation = useLocation();
  const location = props.propLocation || routerLocation;
  const store = useStore().getState();
  const language = store.language || "de";
  const i18n = store.i18n;
  const fileInputRef = useRef(null);
  const pendingItemsRef = useRef([]);
  // nodeid -> timeoutId and nodeid -> { nodeid, fileName }: keyed per attachment
  // so deleting a second item doesn't clobber the first item's pending delete.
  const pendingDeleteTimeoutsRef = useRef({});
  const pendingDeleteItemsRef = useRef({});
  // nodeid -> "pending" | "done": tracks the re-upload (capture-before-purge)
  // so the delete can wait until the file has been safely copied.
  const reuploadStatusRef = useRef({});
  const didHydrateDraftRef = useRef(false);

  const [isDragActive, setIsDragActive] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [deleteNodeId, setDeleteNodeId] = useState("");
  const [pendingItems, setPendingItems] = useState(() =>
    buildLocalDraftItems(props.localDraftAttachments)
  );
  const [persistedOverrides, setPersistedOverrides] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [deletedPersistedIds, setDeletedPersistedIds] = useState([]);
  // nodeid -> { fileid, fileversion, bucketid } from re-uploading a deleted
  // attachment to a fresh minio slot (maintenance edit only).
  const [reuploadedDeletedFiles, setReuploadedDeletedFiles] = useState({});
  const [showMoreImages, setShowMoreImages] = useState(false);
  const [showMoreVideos, setShowMoreVideos] = useState(false);
  const [showMoreDocs, setShowMoreDocs] = useState(false);
  const [undoDeleteState, setUndoDeleteState] = useState({
    open: false,
    nodeid: "",
    fileName: ""
  });
  const [unsupportedFilesToast, setUnsupportedFilesToast] = useState({
    open: false,
    title: "",
    files: []
  });

  const persistedItems = useMemo(
    () => buildPersistedAttachments(props, location, language),
    [props, location, language]
  );

  const visiblePersistedItems = useMemo(
    () => persistedItems.filter(item => !deletedPersistedIds.includes(item.nodeid)),
    [persistedItems, deletedPersistedIds]
  );

  const savedItems = useMemo(
    () =>
      visiblePersistedItems.map(item => ({
          ...item,
          ...(persistedOverrides[item.id] || {})
        })),
    [visiblePersistedItems, persistedOverrides]
  );

  const readonlyDraftItems = useMemo(
    () => buildLocalDraftItems(props.localDraftAttachments),
    [props.localDraftAttachments]
  );

  const readonlyItems = useMemo(
    () => (props.edit ? savedItems : [...readonlyDraftItems, ...savedItems]),
    [props.edit, readonlyDraftItems, savedItems]
  );

  const savedImageItems = useMemo(
    () => readonlyItems.filter(item => item.kind === "image"),
    [readonlyItems]
  );
  const savedVideoItems = useMemo(
    () => readonlyItems.filter(item => item.kind === "video"),
    [readonlyItems]
  );
  const savedDocItems = useMemo(
    () => readonlyItems.filter(item => item.kind === "doc"),
    [readonlyItems]
  );

  const pushCallbackPayload = (
    nextPendingItems,
    nextPersistedOverrides = persistedOverrides
  ) => {
    if (!props.fromChildToParentCallback) return;

    const images = mapPendingToLegacyShape(
      nextPendingItems.filter(item => item.kind === "image")
    );
    const videos = mapPendingToLegacyShape(
      nextPendingItems.filter(item => item.kind === "video")
    );
    const docs = mapPendingToLegacyShape(
      nextPendingItems.filter(item => item.kind === "doc")
    );
    const persistedChanges = buildChangedPersistedAttachments(
      visiblePersistedItems,
      nextPersistedOverrides,
      language
    );
    const deletedAttachments = persistedItems
      .filter(item => deletedPersistedIds.includes(item.nodeid))
      .map(item =>
        buildDeletedAttachmentPayload(
          item.raw,
          language,
          reuploadedDeletedFiles[item.nodeid]
        )
      )
      .filter(Boolean);

    props.fromChildToParentCallback(
      images,
      videos,
      docs,
      props.from,
      persistedChanges,
      deletedAttachments
    );
  };

  const updatePendingItems = updater => {
    setPendingItems(prev => {
      const nextItems = typeof updater === "function" ? updater(prev) : updater;
      pendingItemsRef.current = nextItems;
      pushCallbackPayload(nextItems);
      return nextItems;
    });
  };

  const requestUploadSlots = async files => {
    const userToken = getItem("user");
    const payload = files.map(file => ({
      nodeid: config_UPTIME.url.UPTIME_DEFAULT_URL + uuidv4(),
      extension: getFileExtention(file?.type)
    }));

    const response = await axios.post(
      `${config_UPTIME.url.API_URL}/files`,
      { count: payload.length, extensions: payload },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + JSON.parse(userToken)
        }
      }
    );

    return payload.map((entry, index) => ({
      nodeid: entry.nodeid,
      slot: response.data?.[index]
    }));
  };

  const uploadPendingItem = async (itemId, uploadUrl, file) => {
    try {
      await axios.put(uploadUrl, file, {
        headers: {
          "Content-Type": file.type,
          "Access-Control-Allow-Origin": "*"
        },
        onUploadProgress: event => {
          if (!event.total) return;
          const progress = Math.min(
            100,
            Math.round((event.loaded * 100) / event.total)
          );

          updatePendingItems(prev =>
            prev.map(item =>
              item.id === itemId
                ? {
                    ...item,
                    uploadState: "uploading",
                    uploadProgress: progress,
                    uploading: progress < 100
                  }
                : item
            )
          );
        }
      });

      updatePendingItems(prev =>
        prev.map(item =>
          item.id === itemId
            ? {
                ...item,
                uploadState: "uploaded",
                uploading: false,
                uploaded: true,
                uploadProgress: 100,
                uploadError: ""
              }
            : item
        )
      );
    } catch (error) {
      updatePendingItems(prev =>
        prev.map(item =>
          item.id === itemId
            ? {
                ...item,
                uploadState: "error",
                uploading: false,
                uploaded: false,
                uploadError: "Upload fehlgeschlagen."
              }
            : item
        )
      );
    }
  };

  const addFiles = async fileList => {
    const allFiles = Array.from(fileList || []).filter(Boolean);
    const unsupportedFiles = allFiles.filter(
      file => !isAllowedMimeType(file.type)
    );
    const supportedFiles = allFiles.filter(file => isAllowedMimeType(file.type));
    const oversizedFiles = supportedFiles.filter(
      file => file.size > MAX_FILE_SIZE_BYTES
    );
    const files = supportedFiles.filter(file => file.size <= MAX_FILE_SIZE_BYTES);

    if (unsupportedFiles.length > 0) {
      const unsupportedNames = unsupportedFiles
        .map(file => file.name)
        .filter(Boolean)
        .slice(0, 2);
      const remainingCount = unsupportedFiles.length - unsupportedNames.length;

      setUnsupportedFilesToast({
        open: true,
        title: "Datei nicht unterstützt",
        files: unsupportedNames.length
          ? [
              ...unsupportedNames,
              ...(remainingCount > 0 ? [`${remainingCount} weitere Datei(en)`] : [])
            ]
          : ["Mindestens eine Datei wird nicht unterstützt."]
      });
    }

    if (oversizedFiles.length > 0) {
      const oversizedNames = oversizedFiles
        .map(file => file.name)
        .filter(Boolean)
        .slice(0, 2);
      const remainingCount = oversizedFiles.length - oversizedNames.length;

      setUnsupportedFilesToast({
        open: true,
        title: "Datei zu groß",
        files: oversizedNames.length
          ? [
              ...oversizedNames,
              ...(remainingCount > 0 ? [`${remainingCount} weitere Datei(en)`] : []),
              "Maximal 10 MB pro Datei erlaubt."
            ]
          : ["Maximal 10 MB pro Datei erlaubt."]
      });
    }

    if (!files.length) {
      return;
    }

    const nextItems = files.map((file, index) => ({
      id: uuidv4(),
      nodeid: "",
      index: Date.now() + index,
      sourceType: "local",
      kind: getAttachmentKind(file.type),
      previewUrl: isImageMimeType(file.type) ? URL.createObjectURL(file) : "",
      fileName: file.name,
      title: file.name.replace(/\.[^/.]+$/, ""),
      description: "",
      mime: file.type,
      file,
      url: "",
      fileid: "",
      bucketid: "",
      fileversion: "",
      uploading: true,
      uploaded: false,
      uploadState: "preparing",
      uploadProgress: 0,
      uploadError: ""
    }));

    updatePendingItems(prev => [...prev, ...nextItems]);

    try {
      const slots = await requestUploadSlots(files);

      const validItemIds = nextItems.map(item => item.id);

      updatePendingItems(prev =>
        prev.map(item => {
          const currentIndex = validItemIds.findIndex(id => id === item.id);

          if (currentIndex === -1) {
            return item;
          }

          const slotEntry = slots[currentIndex];
          return {
            ...item,
            nodeid: slotEntry?.nodeid || item.nodeid,
            url: slotEntry?.slot?.url || item.url,
            fileid: slotEntry?.slot?.fileid || item.fileid,
            bucketid: slotEntry?.slot?.bucketid || item.bucketid,
            fileversion: slotEntry?.slot?.fileversion || item.fileversion,
            uploadState: "uploading"
          };
        })
      );

      slots.forEach((slotEntry, index) => {
        const itemId = validItemIds[index];
        const file = nextItems.find(item => item.id === itemId)?.file;
        const uploadUrl = slotEntry?.slot?.url;

        if (itemId && file && uploadUrl) {
          uploadPendingItem(itemId, uploadUrl, file);
        }
      });
    } catch (error) {
      updatePendingItems(prev =>
        prev.map(item =>
          nextItems.some(newItem => newItem.id === item.id) &&
          item.file
            ? {
                ...item,
                uploadState: "error",
                uploading: false,
                uploaded: false,
                uploadError: "Upload konnte nicht vorbereitet werden."
              }
            : item
        )
      );
    }
  };

  const handleInputChange = async event => {
    await addFiles(event.target.files);
    if (event.target) {
      event.target.value = "";
    }
  };

  const handleDrop = async event => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    await addFiles(event.dataTransfer.files);
  };

  const handleDragOver = event => {
    event.preventDefault();
    event.stopPropagation();
    if (!props.edit) return;
    setIsDragActive(true);
  };

  const handleDragLeave = event => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragActive(false);
    }
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteLocal = itemId => {
    updatePendingItems(prev => {
      const target = prev.find(item => item.id === itemId);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter(item => item.id !== itemId);
    });
  };

  const handleDeletePersisted = nodeid => {
    setDeleteNodeId(nodeid);
    setOpenDialogDelete(true);
  };

  const handleDeleteClose = () => {
    setOpenDialogDelete(false);
    setDeleteNodeId("");
  };

  const clearPendingDeleteTimeout = nodeid => {
    if (pendingDeleteTimeoutsRef.current[nodeid]) {
      clearTimeout(pendingDeleteTimeoutsRef.current[nodeid]);
      delete pendingDeleteTimeoutsRef.current[nodeid];
    }
  };

  const performPersistedDeleteRequest = async nodeid => {
    const userToken = getItem("user");

    await axios.delete(
      `${config_UPTIME.url.API_URL}/attachments/${encodeURIComponent(nodeid)}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + JSON.parse(userToken)
        }
      }
    );
  };

  // Re-uploads a deleted attachment's file to a fresh minio slot and records the
  // new fileid/fileversion/bucketid so the `delAttchments` payload references the
  // new copy. Triggered on delete-confirm (before the delete API call) because
  // the delete also removes the object from minio. Best-effort and silent —
  // only used in maintenance edit mode.
  const reuploadDeletedAttachment = async nodeid => {
    reuploadStatusRef.current[nodeid] = "pending";

    const sourceItem =
      persistedItems.find(item => item.nodeid === nodeid) ||
      savedItems.find(item => item.nodeid === nodeid);

    const sourceUrl = sourceItem?.previewUrl;
    if (!sourceUrl) {
      reuploadStatusRef.current[nodeid] = "done";
      return;
    }

    try {
      // Use fetch with credentials:omit — minio signed URLs are anonymous so
      // omitting cookies avoids stricter CORS enforcement that axios triggers.
      const fetchResponse = await fetch(sourceUrl, {
        method: "GET",
        mode: "cors",
        credentials: "omit"
      });
      if (!fetchResponse.ok)
        throw new Error(`Download failed: ${fetchResponse.status}`);
      const blob = await fetchResponse.blob();
      const mimeType = blob.type || sourceItem?.mime || "";
      const file = new File([blob], sourceItem?.fileName || "attachment", {
        type: mimeType
      });

      // request a fresh upload slot from POST /files
      const [slotEntry] = await requestUploadSlots([file]);
      const slot = slotEntry?.slot;
      if (!slot?.url)
        throw new Error("No upload slot URL returned from /files");

      // upload the bytes to the new minio location
      await axios.put(slot.url, file, {
        headers: {
          "Content-Type": file.type,
          "Access-Control-Allow-Origin": "*"
        }
      });

      setReuploadedDeletedFiles(prev => ({
        ...prev,
        [nodeid]: {
          nodeid: slotEntry.nodeid,
          fileid: slot.fileid,
          fileversion: slot.fileversion,
          bucketid: slot.bucketid
        }
      }));
    } catch (error) {
      console.error("[reuploadDeletedAttachment] failed for", nodeid, error);
    } finally {
      reuploadStatusRef.current[nodeid] = "done";
    }
  };

  const finalizePendingDelete = async nodeid => {
    if (!nodeid) return;

    // In re-upload mode, don't delete until the file has been captured &
    // re-uploaded (the delete also purges it from minio). Keep the undo window
    // open and re-check after another 3s window until the copy is ready.
    if (
      props.reuploadDeletedAttachments &&
      reuploadStatusRef.current[nodeid] === "pending"
    ) {
      clearPendingDeleteTimeout(nodeid);
      setUndoDeleteState(prev =>
        prev.nodeid === nodeid ? { ...prev, open: true } : prev
      );
      pendingDeleteTimeoutsRef.current[nodeid] = setTimeout(() => {
        finalizePendingDelete(nodeid);
      }, 3000);
      return;
    }

    clearPendingDeleteTimeout(nodeid);
    delete pendingDeleteItemsRef.current[nodeid];

    try {
      await performPersistedDeleteRequest(nodeid);
    } catch (error) {
      setDeletedPersistedIds(prev => prev.filter(id => id !== nodeid));
      setRowErrors(prev => ({
        ...prev,
        [nodeid]: "Löschen fehlgeschlagen."
      }));
    }

    setUndoDeleteState(prev =>
      prev.nodeid === nodeid
        ? {
            open: false,
            nodeid: "",
            fileName: ""
          }
        : prev
    );
  };

  const schedulePersistedDelete = nodeid => {
    const targetItem = savedItems.find(item => item.nodeid === nodeid);

    // Capture & re-upload the file now (before the delete API removes it from
    // minio) so the `delAttchments` payload can reference the new copy.
    if (props.reuploadDeletedAttachments) {
      reuploadDeletedAttachment(nodeid);
    }

    // Each delete runs independently — don't finalize previously pending items.
    // Re-arming a delete for the same node clears its existing timer first.
    clearPendingDeleteTimeout(nodeid);

    pendingDeleteItemsRef.current[nodeid] = {
      nodeid,
      fileName: targetItem?.fileName || ""
    };

    setDeletedPersistedIds(prev =>
      prev.includes(nodeid) ? prev : [...prev, nodeid]
    );
    // The undo Snackbar shows the most recently deleted item (latest wins);
    // older pending deletes keep running on their own timers.
    setUndoDeleteState({
      open: true,
      nodeid,
      fileName: targetItem?.fileName || ""
    });

    pendingDeleteTimeoutsRef.current[nodeid] = setTimeout(() => {
      finalizePendingDelete(nodeid);
    }, 3000);
  };

  const handleUndoDelete = () => {
    // Undo applies to the item currently shown in the Snackbar.
    const nodeid = undoDeleteState.nodeid;

    if (nodeid) {
      clearPendingDeleteTimeout(nodeid);
      delete pendingDeleteItemsRef.current[nodeid];
      setDeletedPersistedIds(prev => prev.filter(id => id !== nodeid));
      setReuploadedDeletedFiles(prev => {
        if (!prev[nodeid]) return prev;
        const next = { ...prev };
        delete next[nodeid];
        return next;
      });
      delete reuploadStatusRef.current[nodeid];
    }

    setUndoDeleteState({
      open: false,
      nodeid: "",
      fileName: ""
    });
  };

  const updatePendingField = (itemId, field, value) => {
    updatePendingItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const updatePersistedField = (itemId, field, value) => {
    setPersistedOverrides(prev => {
      const nextOverrides = {
        ...prev,
        [itemId]: {
          ...(prev[itemId] || {}),
          [field]: value
        }
      };

      pushCallbackPayload(pendingItemsRef.current, nextOverrides);
      return nextOverrides;
    });
  };

  const renderThumbnail = item => {
    const showUploadOverlay =
      item.sourceType === "local" &&
      !item.uploadError &&
      (item.uploadState === "preparing" || item.uploading);
    const overlayProgress =
      item.uploadState === "preparing" ? 8 : item.uploadProgress || 0;

    const renderOverlay = () =>
      showUploadOverlay ? (
        <>
          <Box
            className={classes.uploadOverlay}
            sx={{ height: `${Math.max(overlayProgress, 8)}%` }}
          >
            <Box className={classes.uploadOverlayLabel}>
              {Math.max(overlayProgress, 0)}%
            </Box>
          </Box>
        </>
      ) : null;

    if (item.kind === "image") {
      return (
        <Box
          className={classes.thumbnail}
          onClick={() => {
            if (item.previewUrl) {
              setLightboxImage(item.previewUrl);
            }
          }}
        >
          <img src={item.previewUrl || photoPlaceholder} alt={item.fileName} />
          {renderOverlay()}
        </Box>
      );
    }

    if (item.kind === "video") {
      return (
        <Box className={classes.thumbnail}>
          <FaFileVideo
            className={cx(
              classes.thumbnailIcon,
              showUploadOverlay ? classes.thumbnailIconUploading : ""
            )}
          />
          {renderOverlay()}
        </Box>
      );
    }

    return (
      <Box className={classes.thumbnail}>
        {renderDocumentIcon(
          item,
          cx(
            classes.thumbnailIcon,
            showUploadOverlay ? classes.thumbnailIconUploading : ""
          )
        )}
        {renderOverlay()}
      </Box>
    );
  };

  const getFileExtensionLabel = fileName => {
    const extension = fileName
      ?.split(".")
      .pop()
      ?.toUpperCase();
    if (!extension || extension === fileName?.toUpperCase()) {
      return "FILE";
    }
    return extension.slice(0, 4);
  };

  const getDocumentIconComponent = item => {
    const extension = getFileExtensionLabel(item?.fileName || "")
      ?.toLowerCase()
      ?.trim();
    const mimeType = (item?.mime || "").toLowerCase();

    if (mimeType === "application/pdf" || extension === "pdf") {
      return BsFiletypePdf;
    }
    if (
      extension === "doc" ||
      mimeType === "application/msword"
    ) {
      return BsFiletypeDoc;
    }
    if (
      extension === "docx" ||
      mimeType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return BsFiletypeDocx;
    }
    if (
      extension === "xls" ||
      mimeType === "application/vnd.ms-excel"
    ) {
      return BsFiletypeXls;
    }
    if (
      extension === "xlsx" ||
      mimeType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      return BsFiletypeXlsx;
    }
    if (
      extension === "ppt" ||
      mimeType === "application/vnd.ms-powerpoint"
    ) {
      return BsFiletypePpt;
    }
    if (
      extension === "pptx" ||
      mimeType ===
        "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    ) {
      return BsFiletypePptx;
    }
    if (extension === "txt" || mimeType === "text/plain") {
      return BsFiletypeTxt;
    }
    return BsFileEarmark;
  };

  const renderDocumentIcon = (item, className) => {
    const IconComponent = getDocumentIconComponent(item);
    return <IconComponent className={className} />;
  };

  const renderUploadMeta = item => {
    if (item.sourceType !== "local") return null;

    if (item.uploadError) {
      return <FormHelperText error>{item.uploadError}</FormHelperText>;
    }

    return null;
  };

  const renderEditableRow = item => {
    const itemId = item.id;
    const titleValue =
      item.sourceType === "local"
        ? item.title || ""
        : persistedOverrides[itemId]?.title ?? item.title ?? "";
    const descriptionValue =
      item.sourceType === "local"
        ? item.description || ""
        : persistedOverrides[itemId]?.description ?? item.description ?? "";

    return (
      <Box
        key={itemId}
        className={cx(
          classes.row,
          item.uploadError ? classes.rowErrorState : "",
          item.nodeid
            ? `dl-${item.nodeid.split("247fn://247uptime.io/")[1]}`
            : ""
        )}
      >
        {renderThumbnail(item)}
        <Box className={classes.rowContent}>
          <Typography className={classes.fileName}>
            {titleValue || item.fileName}
          </Typography>
          <TextField
            fullWidth
            variant="standard"
            multiline
            maxRows={4}
            value={descriptionValue}
            placeholder={i18n.enter_description || "Bezeichnung (optional)..."}
            className={classes.descriptionField}
            onChange={event => {
              const nextValue = event.target.value;
              if (item.sourceType === "local") {
                updatePendingField(itemId, "description", nextValue);
              } else {
                updatePersistedField(itemId, "description", nextValue);
              }
            }}
          />
          {rowErrors[itemId] ? (
            <FormHelperText error>{rowErrors[itemId]}</FormHelperText>
          ) : null}
          {renderUploadMeta(item)}
        </Box>
        <Box className={classes.rowActions}>
          <IconButton
            className={classes.deleteButton}
            onClick={() => {
              if (item.sourceType === "local") {
                handleDeleteLocal(itemId);
              } else {
                handleDeletePersisted(item.nodeid);
              }
            }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
          {/* <DragIndicatorRoundedIcon className={classes.dragHandle} /> */}
        </Box>
      </Box>
    );
  };

  const renderReadonlyPhotos = () => {
    if (!savedImageItems.length) return null;

    const visibleItems = showMoreImages
      ? savedImageItems
      : savedImageItems.slice(0, 3);

    return (
      <Box className={classes.readonlySection}>
        <Typography variant="h2">{i18n.heading_photos}</Typography>
        <Box className={classes.readonlyGrid}>
          {visibleItems.map(item => (
            <Box key={item.id} className={classes.readonlyPhotoCard}>
              <Box
                className={classes.readonlyPhotoThumb}
                onClick={() => setLightboxImage(item.previewUrl)}
              >
                <img
                  src={item.previewUrl || photoPlaceholder}
                  alt={item.fileName}
                />
                <Box className={`${classes.zoomFab} attachment-zoom-fab`}>
                  <Magnifier />
                </Box>
              </Box>
              <Tooltip
                title={item.description}
                placement="bottom-start"
              >
                <Typography className={classes.readonlyDescription}>
                  {truncateWithEllipses(
                    item.description,
                    40
                  )}
                </Typography>
              </Tooltip>
            </Box>
          ))}
        </Box>
        {savedImageItems.length > 3 ? (
          <Box
            className={classes.showMore}
            onClick={() => setShowMoreImages(prev => !prev)}
          >
            <span>
              {showMoreImages
                ? i18n.show_less
                : i18n.show_n_more.replace("%s", savedImageItems.length - 3)}
            </span>
            {showMoreImages ? (
              <ExpandLessRoundedIcon fontSize="small" />
            ) : (
              <ExpandMoreRoundedIcon fontSize="small" />
            )}
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderReadonlyVideos = () => {
    if (!savedVideoItems.length) return null;

    const visibleItems = showMoreVideos
      ? savedVideoItems
      : savedVideoItems.slice(0, 3);

    return (
      <Box className={classes.readonlySection}>
        <Typography variant="h2">{i18n.heading_videos}</Typography>
        <Box className={classes.readonlyGrid}>
          {visibleItems.map(item => (
            <Box key={item.id} className={classes.readonlyVideoCard}>
              <Box className={classes.readonlyVideoFrame}>
                <ReactPlayer
                  width={260}
                  height={170}
                  url={item.previewUrl}
                  controls
                />
              </Box>
              <Tooltip
                title={item.description}
                placement="bottom-start"
              >
                <Typography className={classes.readonlyDescription}>
                  {truncateWithEllipses(
                    item.description,
                    40
                  )}
                </Typography>
              </Tooltip>
            </Box>
          ))}
        </Box>
        {savedVideoItems.length > 3 ? (
          <Box
            className={classes.showMore}
            onClick={() => setShowMoreVideos(prev => !prev)}
          >
            <span>
              {showMoreVideos
                ? i18n.show_less
                : i18n.show_n_more.replace("%s", savedVideoItems.length - 3)}
            </span>
            {showMoreVideos ? (
              <ExpandLessRoundedIcon fontSize="small" />
            ) : (
              <ExpandMoreRoundedIcon fontSize="small" />
            )}
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderReadonlyDocuments = () => {
    if (!savedDocItems.length) return null;

    const visibleItems = showMoreDocs
      ? savedDocItems
      : savedDocItems.slice(0, 3);

    return (
      <Box className={classes.readonlySection}>
        <Typography variant="h2">{i18n.attachments}</Typography>
        <Box className={classes.list}>
          {visibleItems.map(item => (
            <Box
              key={item.id}
              className={classes.readonlyDocRow}
              onClick={() => window.open(item.previewUrl, "_blank")}
            >
              <Box className={classes.readonlyDocThumb}>
                {renderDocumentIcon(item, classes.readonlyDocIcon)}
              </Box>
              <Box className={classes.rowContent}>
                <Typography className={classes.fileName}>
                  {item.fileName}
                </Typography>
                <Typography
                  className={classes.readonlyDescription}
                  sx={{ mt: 0 }}
                >
                  {item.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
        {savedDocItems.length > 3 ? (
          <Box
            className={classes.showMore}
            onClick={() => setShowMoreDocs(prev => !prev)}
          >
            <span>
              {showMoreDocs
                ? i18n.show_less
                : i18n.show_n_more.replace("%s", savedDocItems.length - 3)}
            </span>
            {showMoreDocs ? (
              <ExpandLessRoundedIcon fontSize="small" />
            ) : (
              <ExpandMoreRoundedIcon fontSize="small" />
            )}
          </Box>
        ) : null}
      </Box>
    );
  };

  useEffect(() => {
    pendingItemsRef.current = pendingItems;
  }, [pendingItems]);

  useEffect(() => {
    if (didHydrateDraftRef.current) return;

    const initialDraftItems = buildLocalDraftItems(props.localDraftAttachments);

    if (initialDraftItems.length) {
      didHydrateDraftRef.current = true;
      setPendingItems(initialDraftItems);
      pendingItemsRef.current = initialDraftItems;
    }
  }, [props.localDraftAttachments]);

  useEffect(() => {
    return () => {
      pendingItemsRef.current.forEach(item => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
      Object.keys(pendingDeleteItemsRef.current).forEach(nodeid => {
        performPersistedDeleteRequest(nodeid).catch(() => {});
      });
      Object.keys(pendingDeleteTimeoutsRef.current).forEach(nodeid =>
        clearPendingDeleteTimeout(nodeid)
      );
    };
  }, []);

  useEffect(() => {
    setDeletedPersistedIds(prev =>
      prev.filter(nodeid => persistedItems.some(item => item.nodeid === nodeid))
    );
  }, [persistedItems]);

  // Keep the parent in sync with the set of deleted persisted attachments so the
  // update payload's `delAttchments` reflects undo/redo of deletions.
  useEffect(() => {
    pushCallbackPayload(pendingItemsRef.current);
  }, [deletedPersistedIds, reuploadedDeletedFiles]);

  return (
    <>
      {openDialogDelete ? (
        <DeletePopupByNodeId
          from={props.from}
          nodeid={deleteNodeId}
          open={openDialogDelete}
          rootNodeId={props.rootNodeId}
          close={handleDeleteClose}
          deleteCallback={props?.deleteCallback}
          customDelete={schedulePersistedDelete}
        />
      ) : null}

      {lightboxImage ? (
        <Lightbox
          large={lightboxImage}
          alt={i18n.image_here}
          hideZoom={false}
          showRotate={true}
          onClose={() => setLightboxImage("")}
        />
      ) : null}

      <Grid
        container
        spacing={3}
        direction="row"
        justifyContent="space-between"
      >
        <Grid item xs={12}>
          <Box className={classes.root}>
            {props.edit ? (
              <>
                <Typography className={classes.sectionTitle}>
                  {props.sectionTitle || `${i18n.attachments} (optional)`}
                </Typography>

                <Box
                  className={cx(
                    classes.dropZone,
                    isDragActive ? classes.dropZoneActive : "",
                    props.validationError ? classes.dropZoneError : ""
                  )}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Box className={classes.dropZoneLeft}>
                    <AttachmentIcon className={classes.dropZoneIcon} />
                    <Box className={classes.dropZoneTextBlock}>
                      <Typography className={classes.dropZoneText}>
                        {isDragActive
                          ? "Dateien fallen lassen, um hochzuladen"
                          : "Zum Upload Dateien hier hinziehen"}
                      </Typography>
                      <Typography className={classes.dropZoneSupportingText}>
                        JPG, PNG, DOCX, PDF · Max. 10 MB pro Datei
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="outlined"
                    className={classes.actionButton}
                    onClick={handleOpenFilePicker}
                  >
                    DATEIEN AUSWÄHLEN
                  </Button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={acceptValue}
                    className={classes.hiddenInput}
                    onChange={handleInputChange}
                  />
                </Box>

                {savedItems.length > 0 || pendingItems.length > 0 ? (
                  <Box className={classes.list}>
                    <TransitionGroup component={null}>
                      {pendingItems.map(item => (
                        <Collapse
                          key={item.id}
                          timeout={250}
                          className={classes.transitionItem}
                        >
                          {renderEditableRow(item)}
                        </Collapse>
                      ))}
                      {savedItems.map(item => (
                        <Collapse
                          key={item.id}
                          timeout={250}
                          className={classes.transitionItem}
                        >
                          {renderEditableRow(item)}
                        </Collapse>
                      ))}
                    </TransitionGroup>
                  </Box>
                ) : null}
              </>
            ) : (
              <>
                {renderReadonlyPhotos()}
                {renderReadonlyVideos()}
                {renderReadonlyDocuments()}
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={undoDeleteState.open}
        autoHideDuration={props.reuploadDeletedAttachments ? null : 3000}
        onClose={(event, reason) => {
          if (reason === "clickaway") return;
          setUndoDeleteState(prev => ({
            ...prev,
            open: false
          }));
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box className={classes.snackbarPaper}>
          <Box className={classes.snackbarTitleRow}>
            <CheckCircleOutlineRoundedIcon
              sx={{ color: "#6FA03B", mt: "2px" }}
            />
            <Typography className={classes.snackbarTitle}>
              Datei gelöscht
            </Typography>
          </Box>
          <Box className={classes.snackbarActions}>
            <Button
              className={classes.snackbarUndoButton}
              onClick={handleUndoDelete}
            >
              RÜCKGÄNGIG MACHEN
            </Button>
          </Box>
        </Box>
      </Snackbar>

      <Snackbar
        open={unsupportedFilesToast.open}
        autoHideDuration={3500}
        onClose={(event, reason) => {
          if (reason === "clickaway") return;
          setUnsupportedFilesToast({
            open: false,
            title: "",
            files: []
          });
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box
          className={classes.snackbarPaper}
          sx={{ border: "2px solid #ED6C02 !important", backgroundColor: "#FFF7ED" }}
        >
          <Box className={classes.snackbarTitleRow}>
            <CancelOutlinedIcon
              sx={{ color: "#ED6C02", mt: "2px" }}
            />
            <Typography className={classes.warningSnackbarTitle}>
              {unsupportedFilesToast.title || "Hinweis"}
            </Typography>
          </Box>
          <Box sx={{ mt: 1,ml:1, color: "rgba(0,0,0,0.72)" }}>
            {unsupportedFilesToast.files.map(file => (
              <Typography
                key={file}
                sx={{
                  fontSize: 14,
                  lineHeight: 1.4,
                  display: "flex",
                  alignItems: "flex-start"
                }}
              >
                <Box component="span" sx={{ mr: 1 }}>
                  •
                </Box>
                <Box component="span">{file}</Box>
              </Typography>
            ))}
          </Box>
        </Box>
      </Snackbar>
    </>
  );
}
